import { useEffect } from 'react';
import {
  X, Package, MapPin, Ticket, CreditCard, ShoppingCart,
  Eye, Share2, MessageSquare, Store, PartyPopper, Lightbulb, ArrowLeft,
} from 'lucide-react';

/**
 * WelcomePopup — first-time welcome modal.
 * Congratulates the owner on a newly created business or store.
 *
 * Redesigned to live inside the Mekomit design system: warm cream surface,
 * Soft Olive (brand) accents, Warm Sand (secondary) highlight. Calm, not loud.
 *
 * @param {Object} props
 * @param {string} props.type - 'business' or 'store'
 * @param {string} props.name - business or store name (also accepts `storeName`)
 * @param {Function} props.onClose - close handler
 */
export const WelcomePopup = ({ type = 'store', name, storeName, onClose }) => {
  const displayName = name || storeName;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const businessFeatures = [
    {
      icon: Eye,
      title: 'אתם כבר נראים!',
      description: 'העסק שלכם כבר מופיע לכל משתמשי המקומית באזורים שבחרתם',
    },
    {
      icon: Share2,
      title: 'שתפו את העסק',
      description: 'שתפו את העסק שלכם עם חברים וקהילה כדי להגדיל את החשיפה',
    },
    {
      icon: MessageSquare,
      title: 'אספו ביקורות',
      description: 'עודדו לקוחות לתת ביקורות - ככל שיש יותר ביקורות, העסק יומלץ יותר על ידי הבינה המלאכותית של המקומית',
    },
    {
      icon: Store,
      title: 'צרו חנות מקוונת',
      description: 'ניתן ליצור חנות מקוונת בקלות ולנהל מוצרים והזמנות ישירות מהעסק',
    },
  ];

  const storeFeatures = [
    {
      icon: Package,
      title: 'יצירת מוצרים',
      description: 'הוסף והגדר מוצרים עם תמונות, מחירים ותיאורים',
    },
    {
      icon: MapPin,
      title: 'נקודות חלוקה ואיזורים',
      description: 'הגדר נקודות חלוקה ואזורי משלוחים ללקוחות שלך',
    },
    {
      icon: Ticket,
      title: 'קופונים',
      description: 'צור קופוני הנחה למשוך לקוחות ולתגמל נאמנים',
    },
    {
      icon: CreditCard,
      title: 'אמצעי תשלום',
      description: 'הגדר את אפשרויות התשלום המתאימות לעסק שלך',
    },
    {
      icon: ShoppingCart,
      title: 'ניהול הזמנות',
      description: 'עקוב אחרי הזמנות ועדכן סטטוסים בזמן אמת',
    },
  ];

  const features = type === 'business' ? businessFeatures : storeFeatures;

  return (
    <div
      className="fixed inset-0 z-modal bg-ink/45 backdrop-blur-[3px] flex items-start md:items-center justify-center p-4 md:py-8 animate-fade-in overflow-y-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-surface rounded-2xl max-w-2xl w-full shadow-elevated border border-line-subtle animate-scale-in my-4 md:my-8 relative max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        {/* Header — calm warm gradient, no rainbow */}
        <div className="relative px-8 pt-10 pb-8 text-center bg-gradient-to-b from-brand-50 via-surface to-surface">
          <button
            onClick={onClose}
            className="absolute top-4 left-4 inline-flex items-center justify-center w-9 h-9 rounded-md text-ink-subtle hover:text-ink hover:bg-surface-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            aria-label="סגור"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="mx-auto mb-5 w-16 h-16 rounded-pill bg-surface shadow-md ring-1 ring-line-subtle flex items-center justify-center">
            <PartyPopper className="w-8 h-8 text-brand-500" />
          </div>

          <h2 className="text-3xl font-bold text-ink-strong mb-2">
            מזל טוב!
          </h2>
          <p className="text-base text-ink-muted">
            {type === 'business' ? 'העסק' : 'החנות'}{' '}
            <span className="font-semibold text-ink">{displayName}</span>{' '}
            {type === 'business' ? 'נוצר בהצלחה' : 'נוצרה בהצלחה'}
          </p>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-8 pb-6">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-ink-strong mb-1">
              {type === 'business' ? 'מה הלאה?' : 'מה אפשר לעשות בחנות?'}
            </h3>
            <p className="text-sm text-ink-muted">
              {type === 'business'
                ? 'הנה כמה דברים חשובים שכדאי לדעת'
                : 'הנה תצוגה מקדימה של האפשרויות שעומדות לרשותך'}
            </p>
          </div>

          {/* Features — unified, on-palette. Subtle brand / sand alternation. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isSand = index % 2 === 1;
              return (
                <div
                  key={index}
                  className="rounded-xl border border-line-subtle bg-surface-muted/60 p-5 transition-colors duration-200 hover:border-brand-300 hover:bg-brand-50/50"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${
                        isSand
                          ? 'bg-secondary-100 text-secondary-700'
                          : 'bg-brand-100 text-brand-700'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-ink mb-1">
                        {feature.title}
                      </h4>
                      <p className="text-sm text-ink-muted leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footnote — single warm-sand highlight */}
          <div className="flex items-start gap-3 rounded-xl bg-secondary-50 border border-secondary-200 px-5 py-4">
            <Lightbulb className="w-5 h-5 text-secondary-600 shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed">
              {type === 'business' ? (
                <>
                  <span className="font-semibold text-ink">חשוב לדעת: </span>
                  <span className="text-ink-muted">
                    העסק שלכם כבר חי ופעיל — משתמשים באזורים שבחרתם יכולים לראות אתכם כבר עכשיו.
                  </span>
                </>
              ) : (
                <>
                  <span className="font-semibold text-ink">טיפ: </span>
                  <span className="text-ink-muted">
                    מומלץ להתחיל בהוספת מוצרים והגדרת נקודות חלוקה. תוכל תמיד לצפות בחנות שלך דרך כפתור "לצפייה בחנות" בראש העמוד.
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Fixed bottom action */}
        <div className="px-6 py-4 border-t border-line-subtle bg-surface-muted/40">
          <button
            onClick={onClose}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-brand-500 text-ink-inverse rounded-lg hover:bg-brand-600 active:bg-brand-700 active:scale-[0.98] transition-all duration-200 shadow-sm hover:shadow-md font-semibold text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            בואו נתחיל!
            <ArrowLeft className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
